import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { useAuthStore } from "@/store/auth.store";

type MagicLoginStatus = "missing-code" | "pending" | "success" | "error";

interface AttemptResult {
  code: string;
  ok: boolean;
}

interface UseMagicLoginResult {
  status: MagicLoginStatus;
  targetUrl: string;
}

const DEFAULT_TARGET_URL = ROUTES.PAYWALL("default");

const resolveSafeTargetUrl = (raw: string | null): string => {
  if (!raw?.startsWith("/") || raw.startsWith("//")) {
    return DEFAULT_TARGET_URL;
  }
  try {
    const base = "http://localhost";
    const resolved = new URL(raw, base);
    if (resolved.origin !== base) return DEFAULT_TARGET_URL;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return DEFAULT_TARGET_URL;
  }
};

export const useMagicLogin = (): UseMagicLoginResult => {
  const { t } = useTranslation("Common");
  const [searchParams] = useSearchParams();
  const magicLogin = useAuthStore((state) => state.magicLogin);

  const startedCode = useRef<string | null>(null);
  const [result, setResult] = useState<AttemptResult | null>(null);

  const code = searchParams.get("code");
  const targetUrl = resolveSafeTargetUrl(searchParams.get("target_url"));

  useEffect(() => {
    if (!code || startedCode.current === code) return;
    startedCode.current = code;

    const runMagicLogin = async () => {
      try {
        await magicLogin(code);
        if (startedCode.current === code) setResult({ code, ok: true });
      } catch {
        if (startedCode.current === code) {
          setResult({ code, ok: false });
          toast.error(t("MagicLink.error"));
        }
      }
    };
    runMagicLogin();
  }, [code, magicLogin]);

  if (!code) {
    return { status: "missing-code", targetUrl };
  }

  const activeResult = result?.code === code ? result : null;
  if (!activeResult) {
    return { status: "pending", targetUrl };
  }

  return { status: activeResult.ok ? "success" : "error", targetUrl };
};
